import React, { useState, useEffect } from 'react'
import RedeemIcon from '@material-ui/icons/Redeem'
import CakeIcon from '@material-ui/icons/Cake'
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered'
import Tooltip from '@material-ui/core/Tooltip'
import Grid from '@material-ui/core/Grid'
import Input from '@material-ui/core/Input'
import AddIcon from '@material-ui/icons/Add'
import { Button, Typography } from '@material-ui/core'

import { ERRORS, PRIZE } from 'consts'
import { useApi, useApiCall } from 'hooks'
import { snackbar } from 'components'
import { CustomCheckbox } from 'components/CustomCheckbox'

import { getPrizes, updatePrize } from './api'
import {
  GetPrizesResponse,
  Prize,
  UpdatePrizeResponse,
  UpdatePrizeData,
  CreatePrizePayload,
} from './types'
import { useStyles } from './styles'
// import { CreatePrizeDialog, DeletePrizeDialog, UpdatePrizeDialog } from './components'
// import CloudUploadIcon from '@material-ui/icons/CloudUpload'

const getPrizesResponseGetter = (responseData: GetPrizesResponse) => responseData?.prizes ?? []

const Prizes: React.FC = () => {
  const [{ data: prizes }, , setPrizes] = useApi<GetPrizesResponse, Prize[]>(
    getPrizes,
    getPrizesResponseGetter,
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )

  const classes = useStyles()

  const setRewardType = (prize: Prize) => {
    if (prize.level)
      return (
        <>
          <Tooltip title="Premio por Nivel">
            <FormatListNumberedIcon color="primary" />
          </Tooltip>{' '}
          Nivel {prize.level}
        </>
      )
    if (prize.welcomePrize)
      return (
        <>
          <Tooltip title="Premio de bienvenida">
            <RedeemIcon color="primary" />
          </Tooltip>{' '}
          Reward de bienvenida
        </>
      )
    if (prize.birthdayPrize)
      return (
        <>
          <Tooltip title="Premio de cumpleaños">
            <CakeIcon color="primary" />
          </Tooltip>{' '}
          Reward de cumpleaños
        </>
      )
    return (
      <>
        <Tooltip title="Reward manual">
          <RedeemIcon color="primary" />
        </Tooltip>{' '}
        Reward manual
      </>
    )
  }

  const [updatePrizeApi] = useApiCall<UpdatePrizeData, UpdatePrizeResponse>(updatePrize)
  const [inputValue, setInputValue] = useState('')
  const [currentPrize, setCurrentPrize] = useState<Prize>()
  const [inputField, setInputField] = useState('')

  const handleCreatePrizeDone = (updatedPrice: Prize) => {
    setPrizes(
      prizes.map(prize => {
        if (updatedPrice.id === prize.id) {
          return updatedPrice
        }
        return prize
      }),
    )
  }

  const onUpdatePrize = async (prize: Prize, field: string, value?: string | File) => {
    const payload: Partial<CreatePrizePayload> = {
      title: prize.title || '',
      shortTitle: prize.shortTitle || '',
      level: prize.level,
      rewardType: PRIZE.REWARD_TYPE.AUTOMATIC,
      welcomePrize: prize.welcomePrize,
      birthdayPrize: prize.birthdayPrize || undefined,
      active: prize.active,
      additionalText: prize.additionalText || '',
    }

    if (field === 'active') payload.active = !payload.active
    if (field === 'title' && typeof value === 'string') payload.title = value
    if (field === 'shortTitle' && typeof value === 'string') payload.shortTitle = value
    if (field === 'icon' && value instanceof File) payload.icon = value
    if (field === 'additionalText' && typeof value === 'string') payload.additionalText = value

    try {
      const { prize: updatedPrice } = await updatePrizeApi({ id: prize.id, payload })
      handleCreatePrizeDone(updatedPrice)
      snackbar.show(`Premio ${updatedPrice.shortTitle || updatedPrice.title} modificado`)
    } catch (err) {
      snackbar.show('No se pudo editar reward. Intente de nuevo.')
    }
    setInputValue('')
  }

  useEffect(() => {
    if (!currentPrize) return
    const timer = setTimeout(() => {
      onUpdatePrize(currentPrize, inputField, inputValue)
    }, 500)

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, currentPrize, inputField])

  const handleIconChange = (event: React.ChangeEvent<HTMLInputElement>, prize: Prize) => {
    if (!event.target.files?.length) return
    const file = event.target.files[0]
    onUpdatePrize(prize, 'icon', file)
  }

  const handleChangeTitle = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    prize: Prize,
  ) => {
    const { value } = event.currentTarget
    setCurrentPrize(prize)
    setInputField('title')
    setInputValue(value)
  }

  const handleChangeShortTitle = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    prize: Prize,
  ) => {
    const { value } = event.currentTarget
    if (!value) {
      snackbar.show('Este campo no puede estar vacio.')
      return
    }
    if (value.length > 22) {
      snackbar.show('El máximo de carácteres permitidos es 22.')
      return
    }
    setCurrentPrize(prize)
    setInputField('shortTitle')
    setInputValue(value)
  }

  const handleChangeAdditionalText = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    prize: Prize,
  ) => {
    const { value } = event.currentTarget
    if (!value) {
      snackbar.show('Este campo no puede estar vacio.')
      return
    }
    setCurrentPrize(prize)
    setInputField('additionalText')
    setInputValue(value)
  }

  const handleChangeActive = async (event: React.ChangeEvent<HTMLInputElement>, prize: Prize) => {
    await onUpdatePrize(prize, 'active')
    window.location.reload()
  }

  const enabledAutomaticPrizesFilter = (prize: Prize) => prize.welcomePrize

  return (
    <>
      <div className={classes.root}>
        <Grid container spacing={4} item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} className={classes.title}>
              <Typography variant="h6">Rewards automatizadas</Typography>
            </Grid>
            {prizes.filter(enabledAutomaticPrizesFilter).map(prize => (
              <Grid container item xs={12} spacing={4} key={`key_${prize.id}`}>
                <Grid container item xs={12} spacing={0} className={classes.rewardType}>
                  <Grid item xs={1} className={classes.checkbox}>
                    <CustomCheckbox
                      id={`active_${prize.id}`}
                      checked={prize.active}
                      onChange={event => handleChangeActive(event, prize)}
                      inputProps={{ 'aria-label': 'Checkbox A' }}
                    />
                  </Grid>
                  <Grid item xs={10}>
                    {setRewardType(prize)}
                  </Grid>
                </Grid>
                <Grid item xs={12} container spacing={1}>
                  <Grid item xs={1} />
                  <Grid item xs={7}>
                    <Grid item xs={12}>
                      <Input
                        id={`title_${prize.id}`}
                        defaultValue={prize.title}
                        inputProps={{ 'aria-label': 'description' }}
                        placeholder="NOMBRE REWARD"
                        className={classes.input}
                        onChange={event => handleChangeTitle(event, prize)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Input
                        id={`shortTitle_${prize.id}`}
                        defaultValue={prize.shortTitle}
                        inputProps={{ 'aria-label': 'description' }}
                        placeholder="ACLARACIÓN"
                        className={classes.input}
                        onChange={event => handleChangeShortTitle(event, prize)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Input
                        id={`additionalText_${prize.id}`}
                        defaultValue={prize.additionalText}
                        inputProps={{ 'aria-label': 'description' }}
                        placeholder="TEXTO ADICIONAL"
                        className={classes.input}
                        onChange={event => handleChangeAdditionalText(event, prize)}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={4} container alignItems="center" className={classes.iconSection}>
                    <div className={classes.iconUploadContainer}>
                      <label htmlFor={`icon_${prize.id}`}>
                        <input
                          style={{ display: 'none' }}
                          id={`icon_${prize.id}`}
                          accept=".jpg, .jpeg, .png"
                          type="file"
                          onChange={event => handleIconChange(event, prize)}
                        />
                        <Tooltip title="Formatos compatibles: .jpg, .jpeg, .png" placement="bottom">
                          <Button
                            variant="contained"
                            color="primary"
                            component="span"
                            startIcon={<AddIcon />}
                          >
                            Icono
                          </Button>
                        </Tooltip>
                      </label>
                      <div className={classes.iconUploadPreviewContainer}>
                        <img src={prize.iconUrl} alt="" className={classes.iconPreview} />
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </div>
    </>
  )
}

export { Prizes }
